import type { TyreLayoutSlot } from '@chukua/common';
import { Button, Tag, type TableColumnsType } from 'antd';
import { Link } from 'react-router';
import { formatDepth, formatKm, TYRE_STATUS_COLORS, TYRE_STATUS_LABELS } from '../policies/tyre-labels';
import { tyrePath } from '../tyres.paths';

interface SlotActionsProps extends Omit<BuildVehicleTyreColumnsArgs, 'canManage'> {
  slot: TyreLayoutSlot;
}

/** Replace and Rotate for a filled position, Fit for an empty one (not a retired one, which is not on the layout any more). */
function SlotActions({ slot, onFit, onReplace, onRotate }: SlotActionsProps) {
  if (!slot.tyre) {
    return (
      <Button size="small" disabled={slot.isRetired} onClick={() => onFit(slot)}>
        Fit a tyre
      </Button>
    );
  }
  return (
    <div className="flex gap-1">
      <Button size="small" onClick={() => onReplace(slot)}>
        Replace
      </Button>
      <Button size="small" onClick={() => onRotate(slot)}>
        Rotate
      </Button>
    </div>
  );
}

interface BuildVehicleTyreColumnsArgs {
  canManage: boolean;
  onFit: (slot: TyreLayoutSlot) => void;
  onReplace: (slot: TyreLayoutSlot) => void;
  onRotate: (slot: TyreLayoutSlot) => void;
}

/** Position, tyre, status, tread and run for each slot, and (with `tyre.manage`) the actions that start from a position. */
export function buildVehicleTyreColumns({ canManage, onFit, onReplace, onRotate }: BuildVehicleTyreColumnsArgs): TableColumnsType<TyreLayoutSlot> {
  const columns: TableColumnsType<TyreLayoutSlot> = [
    {
      title: 'Position',
      key: 'position',
      render: (_, slot) => (
        <span>
          <span className="font-mono">{slot.positionCode}</span>
          {slot.isRetired && (
            <Tag className="ml-2" color="warning">
              Retired position
            </Tag>
          )}
        </span>
      ),
    },
    {
      title: 'Tyre',
      key: 'tyre',
      render: (_, slot) =>
        slot.tyre ? (
          <Link to={tyrePath(slot.tyre.id)} className="font-mono">
            {slot.tyre.serial}
          </Link>
        ) : (
          <span className="text-muted">Empty</span>
        ),
    },
    { title: 'Brand', key: 'brand', render: (_, slot) => (slot.tyre ? `${slot.tyre.brand} ${slot.tyre.size}` : '—') },
    {
      title: 'Status',
      key: 'status',
      render: (_, slot) => (slot.tyre ? <Tag color={TYRE_STATUS_COLORS[slot.tyre.status]}>{TYRE_STATUS_LABELS[slot.tyre.status]}</Tag> : '—'),
    },
    { title: 'Tread', key: 'tread', render: (_, slot) => formatDepth(slot.tyre?.currentDepthMm ?? null) },
    { title: 'Run', key: 'run', render: (_, slot) => formatKm(slot.tyre?.kmOnTyre ?? null) },
  ];
  if (canManage)
    columns.push({ title: '', key: 'actions', render: (_, slot) => <SlotActions slot={slot} onFit={onFit} onReplace={onReplace} onRotate={onRotate} /> });
  return columns;
}
